import { z } from 'zod';
import type { ConnectionStatus } from './models';
import { CONNECTION_PROVIDERS, type ConnectionProviderId } from './sources';

/**
 * Authenticated source connections.
 *
 * Secrets travel to the API once, in a request body, and are encrypted before storage. They are
 * never returned: the only thing the UI ever sees again is a short, non-reversible hint.
 */
export const SaveConnectionSchema = z.object({
  provider: z.enum(CONNECTION_PROVIDERS),
  secret: z.string().trim().min(1, 'Paste the key or token.').max(8000),
  label: z.string().trim().max(100).optional(),
});
export type SaveConnectionRequest = z.input<typeof SaveConnectionSchema>;

export interface ConnectionDto {
  provider: ConnectionProviderId;
  status: ConnectionStatus;
  label: string | null;
  /** Non-reversible display hint such as "AIza…4f2Q". Never the secret itself. */
  hint: string | null;
  createdAt: number;
  updatedAt: number;
  /** Null until a real request to the provider has succeeded. */
  lastVerifiedAt: number | null;
  expiresAt: number | null;
  lastError: string | null;
}

export interface ConnectionProviderInfo {
  label: string;
  /** What to paste, and what it costs or implies. */
  help: string;
  placeholder: string;
}

export const CONNECTION_PROVIDER_INFO: Record<ConnectionProviderId, ConnectionProviderInfo> = {
  google_places: {
    label: 'Google Places API key',
    help: 'Create a key in Google Cloud with the Places API (New) enabled. Google bills your own account per request.',
    placeholder: 'AIza...',
  },
  google_sheets: {
    label: 'Google service account key',
    help: 'Google Cloud → IAM → Service accounts → Keys → Add key → JSON, with the Google Sheets API enabled. Paste the whole file. Then share each spreadsheet you want to write to with the service account\'s email address, giving it Editor access.',
    placeholder: '{ "type": "service_account", "project_id": "...", "private_key": "-----BEGIN PRIVATE KEY-----\\n..." }',
  },
  gmail: {
    label: 'Gmail sending account',
    help: 'The Google OAuth client and refresh token for the address outreach is sent from. Use the same Google client as the 2.0 contact form, and mint a refresh token with scope gmail.send. Revoking access in the Google account stops sending immediately.',
    placeholder: '{ "client_id": "...", "client_secret": "...", "refresh_token": "...", "sender": "you@elevate2point0.com" }',
  },
  anthropic: {
    label: 'Anthropic API key',
    help: 'Used for custom natural-language attributes and the research assistant. Billed to your own Anthropic account.',
    placeholder: 'sk-ant-...',
  },
  gemini: {
    label: 'Google Gemini API key',
    help: 'Answers custom questions the deterministic rules cannot (hiring, delivery, branch count, multi-city and role lookups never need this — they work without any key). Billed to your own Google Cloud account.',
    placeholder: 'AIza...',
  },
};

export const CONNECTION_STATUS_LABELS: Record<ConnectionStatus, string> = {
  connected: 'Connected',
  invalid: 'Invalid',
  expired: 'Expired',
  revoked: 'Revoked',
};
